import FilterListIcon from '@mui/icons-material/FilterList';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import StarHalfRoundedIcon from '@mui/icons-material/StarHalfRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import UnfoldMoreRoundedIcon from '@mui/icons-material/UnfoldMoreRounded';
import UnfoldLessRoundedIcon from '@mui/icons-material/UnfoldLessRounded';
import FullscreenRoundedIcon from '@mui/icons-material/FullscreenRounded';
import FullscreenExitRoundedIcon from '@mui/icons-material/FullscreenExitRounded';
import {
  Badge,
  Box,
  Button,
  Grow,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  GridToolbar,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid-premium';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { presetKeys } from '@/features/portfolio-analysis/utils/pa-presets';
import { useLocation, useNavigate } from 'react-router-dom';
import useSearchParams from '@/hooks/useStableSearchParams';
const PortfolioAnalysisToolbar = ({
  handleFavoritesFilterClick,
  isFavoritesFiltered,
  setIsFavoritesFiltered,
  handleExpandCollapse,
  isCollapsed,
  filterModel = [],
}) => {
  const [showToolbar, setShowToolbar] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const isFullScreen = searchParams.get('fullscreen') === 'true';
  const handleFullScreenToggle = () => {
    if (isFullScreen) {
      searchParams.delete('fullscreen');
    } else {
      searchParams.set('fullscreen', 'true');
    }
    setSearchParams(searchParams);
  };

  const { t } = useTranslation(`portfolio-analysis`);

  const location = useLocation();

  const navigate = useNavigate();

  const analysisPreset = presetKeys.find((preset) =>
    location.pathname.includes(preset),
  );

  const analysisType = location.pathname.includes('power')
    ? 'power'
    : location.pathname.includes('heat')
      ? 'heat'
      : location.pathname.includes('water')
        ? 'water'
        : '';

  const presetItems = presetKeys.map((preset) => ({
    label: t(`presets.${preset}`, {
      ns: `portfolio-analysis_${analysisType}`,
    }),
    path: `${analysisType}/${preset}`,
    key: preset,
  }));
  return (
    <GridToolbarContainer
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 1,
        background: 'inherit',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <GridToolbarQuickFilter
          size="small"
          sx={{
            '& .MuiInputBase-root': {
              backgroundColor: (theme) => theme.palette.background.paper,
              borderRadius: 2,
            },
            '& .MuiFilledInput-root': {
              borderRadius: '5px',
            },
            '& .MuiInput-underline:before, & .MuiInput-underline:after': {
              display: 'none',
            },
            '& .MuiInputLabel-root': {
              color: 'gray',
            },
          }}
          variant="outlined"
          InputProps={{
            placeholder: 'Søg...',
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon sx={{ color: 'gray' }} />
              </InputAdornment>
            ),
          }}
        />
        {analysisType && (
          <Box>
            <ToggleButtonGroup
              value={analysisPreset}
              sx={{ backgroundColor: 'background.paper', ml: 2 }}
            >
              {presetItems.map((p) => {
                return (
                  <ToggleButton
                    variant="outlined"
                    size="small"
                    value={p.key}
                    onClick={() =>
                      navigate(
                        `/portfolio-analysis-hub/${p.path}?${searchParams.toString()}`,
                      )
                    }
                    key={p.key}
                    sx={{
                      borderRadius: 2,
                      p: 0.9,
                      mt: -0.5,
                      color: 'text.primary',
                    }}
                  >
                    {p.label}
                  </ToggleButton>
                );
              })}
            </ToggleButtonGroup>
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Grow in={showToolbar}>
          <GridToolbar />
        </Grow>
        <Badge
          color="primary"
          badgeContent={filterModel.items.length}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{
            badge: {
              sx: {
                // Pull the badge INSIDE the button so it won't be clipped
                top: -7,
                right: -7,
                transform: 'none', // cancel default translate(50%, -50%)
                zIndex: 1,
              },
            },
          }}
        >
          <Button
            startIcon={showToolbar ? <FilterListOffIcon /> : <FilterListIcon />}
            variant="outlined"
            size="small"
            onClick={() => setShowToolbar(!showToolbar)}
            sx={{
              borderRadius: 2,
              borderColor: 'divider',
              backgroundColor: 'background.paper',
              textTransform: 'none',
              p: 0.9,
              mt: -0.5,
              color: 'text.primary',
            }}
          >
            {showToolbar
              ? t('toolbar.hide_settings')
              : t('toolbar.show_settings')}
          </Button>
        </Badge>
        <Button
          startIcon={
            isFullScreen ? (
              <FullscreenExitRoundedIcon />
            ) : (
              <FullscreenRoundedIcon />
            )
          }
          variant="outlined"
          size="small"
          onClick={handleFullScreenToggle}
          sx={{
            borderRadius: 2,
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            textTransform: 'none',
            p: 0.9,
            mt: -0.5,
            ml: 2,
            color: 'text.primary',
          }}
        >
          {isFullScreen
            ? t('toolbar.fullscreen_exit')
            : t('toolbar.fullscreen_enter')}
        </Button>
        <Button
          startIcon={
            isCollapsed ? <UnfoldMoreRoundedIcon /> : <UnfoldLessRoundedIcon />
          }
          variant="outlined"
          size="small"
          onClick={handleExpandCollapse}
          sx={{
            borderRadius: 2,
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            textTransform: 'none',
            p: 0.9,
            mt: -0.5,
            ml: 2,
            color: 'text.primary',
          }}
        >
          {isCollapsed ? t('toolbar.expand_all') : t('toolbar.collapse_all')}
        </Button>
        <Button
          startIcon={
            isFavoritesFiltered ? (
              <StarRoundedIcon color="warning" />
            ) : (
              <StarHalfRoundedIcon />
            )
          }
          variant="outlined"
          size="small"
          onClick={() => {
            handleFavoritesFilterClick(!isFavoritesFiltered);
            setIsFavoritesFiltered(!isFavoritesFiltered);
          }}
          sx={{
            borderRadius: 2,
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            textTransform: 'none',
            p: 0.9,
            mt: -0.5,
            ml: 2,
            color: 'text.primary',
          }}
        >
          {t('toolbar.only_favorites')}
        </Button>
      </Box>
    </GridToolbarContainer>
  );
};

export default PortfolioAnalysisToolbar;
